import React, { FunctionComponent, ReactNode } from "react";

import { bemClassNames, createQA } from "@multiweb/reactUtils";
import { QAProps } from "@multiweb/types/react";
import { AlignItems } from "../types";
import { Display, FlexDirection, FlexGrow, FlexShrink, FlexWrap, JustifyContent, Position, SpacingSize } from "./enums";

export type LayoutProps = {
    /** Pozicování vůči vedlejší ose */
    alignItems?: AlignItems;
    /** Obalovaná komponenta */
    children?: ReactNode;
    /** Vynucené zhroucení obsahu */
    collapse?: boolean;
    /** Způsob rozložení prvku */
    display?: Display;
    /**
     * Příznak, zda má layout vyplnit výšku nadřazeného prvku.
     *
     * **Upozornění:** nastavit pouze pro první flex container! Více info v Layout.md
     */
    fillHeight?: boolean;
    /** Směr skládání prvků */
    flexDirection?: FlexDirection;
    /** Podíl velikosti rodiče */
    flexGrow?: FlexGrow;
    /** Smršťování obsahu */
    flexShrink?: FlexShrink;
    /** Zalamování obsahu */
    flexWrap?: FlexWrap;
    /** Pozicování vůči hlavní ose */
    justifyContent?: JustifyContent;
    /** Velikost odsazení ze všech stran*/
    spacingSize?: SpacingSize;
    /** Velikost spodního odsazení */
    spacingSizeBottom?: SpacingSize;
    /** Velikost horizontálního odsazení */
    spacingSizeHorizontal?: SpacingSize;
    /** Velikost levého odsazení */
    spacingSizeLeft?: SpacingSize;
    /** Velikost pravého odsazení */
    spacingSizeRight?: SpacingSize;
    /** Velikost horního odsazení */
    spacingSizeTop?: SpacingSize;
    /** Velikost vertikálního odsazení */
    spacingSizeVertical?: SpacingSize;
    /** Odsazení mezi flex itemy, hodnota je v pixelech */
    gap?: number;
    /** Příznak, zda se má odebrat prvek a všechny jeho podřízené prvky ze stromu přístupnosti */
    ariaHidden?: boolean;
    /** Pozice vůči celé stránce */
    position?: Position;
} & QAProps;

const classNames = bemClassNames("layout-component");

/**
 * Komponenta slouží pro rozložení komponent. Umožňuje nastavit způsob rozložení prvků (`display`)
 * a odsazení okolo obalovaných. Při použití zobrazení prvků `flex` umožňuje nastavit další vlastnosti.
 * */
export const Layout: FunctionComponent<LayoutProps> = ({
    alignItems,
    children,
    collapse,
    display = Display.flex,
    fillHeight = false,
    flexDirection,
    flexGrow,
    flexShrink,
    flexWrap,
    gap,
    justifyContent,
    spacingSize,
    spacingSizeBottom,
    spacingSizeHorizontal,
    spacingSizeLeft,
    spacingSizeRight,
    spacingSizeTop,
    spacingSizeVertical,
    qa,
    ariaHidden,
    position,
}) => (
    <div
        className={classNames({
            "align-items": alignItems,
            collapse,
            display,
            "fill-height": fillHeight,
            "flex-direction": flexDirection,
            "flex-grow": flexGrow,
            "flex-shrink": flexShrink,
            "flex-wrap": flexWrap,
            "justify-content": justifyContent,
            "spacing-size-all": spacingSize,
            "spacing-size-bottom": spacingSizeBottom,
            "spacing-size-horizontal": spacingSizeHorizontal,
            "spacing-size-left": spacingSizeLeft,
            "spacing-size-right": spacingSizeRight,
            "spacing-size-top": spacingSizeTop,
            "spacing-size-vertical": spacingSizeVertical,
            position,
        })}
        aria-hidden={ariaHidden}
        style={{ gap }}
        {...createQA(qa)}
    >
        {children}
    </div>
);
